// Colocated leaf components and style constants for ConversationalPath.
// Kept in a sibling file to keep ConversationalPath under the 500-line cap.

import { Info } from 'lucide-react';
import {
  admin,
  palette,
  radii,
  typography,
} from '../../../../styles/tokens';

export const inputStyle: React.CSSProperties = {
  padding: '8px 10px',
  border: `1px solid ${palette.g200}`,
  borderRadius: radii.xs,
  fontSize: typography.size.body,
  fontFamily: 'inherit',
  color: admin.header,
  background: palette.white,
  outline: 'none',
};

export const backButtonStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '6px 8px 6px 4px',
  marginBottom: 16,
  background: 'transparent',
  border: 'none',
  color: admin.caption,
  fontSize: typography.size.caption,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

export function Banner({
  copy,
  onSwitch,
  onDismiss,
}: {
  copy: string;
  onSwitch: () => void;
  onDismiss: () => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        padding: 14,
        background: palette.g50,
        border: `1px solid ${palette.g150}`,
        borderRadius: radii.sm,
        marginBottom: 16,
      }}
    >
      <Info size={16} color={palette.g500} style={{ flexShrink: 0, marginTop: 2 }} />
      <div style={{ flex: 1 }}>
        <div
          style={{
            fontSize: typography.size.caption,
            color: admin.body,
            lineHeight: typography.lineHeight.relaxed,
            marginBottom: 8,
          }}
        >
          {copy}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <InlineButton onClick={onSwitch}>Switch to spreadsheet</InlineButton>
          <InlineButton onClick={onDismiss} muted>
            Keep typing
          </InlineButton>
        </div>
      </div>
    </div>
  );
}

export function RevealButton({
  onClick,
  icon,
  children,
}: {
  onClick: () => void;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '6px 10px',
        background: palette.white,
        border: `1px solid ${palette.g200}`,
        borderRadius: radii.xs,
        fontSize: typography.size.caption,
        color: admin.caption,
        cursor: 'pointer',
        fontFamily: 'inherit',
      }}
    >
      {icon}
      {children}
    </button>
  );
}

export function InlineButton({
  onClick,
  children,
  muted = false,
}: {
  onClick: () => void;
  children: React.ReactNode;
  muted?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '6px 12px',
        background: muted ? palette.white : palette.accent,
        color: muted ? admin.body : palette.white,
        border: `1px solid ${muted ? palette.g200 : palette.accent}`,
        borderRadius: radii.xs,
        fontSize: typography.size.caption,
        fontWeight: typography.weight.semibold,
        fontFamily: 'inherit',
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

export function IconButton({
  onClick,
  label,
  children,
}: {
  onClick: () => void;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 26,
        height: 26,
        background: 'transparent',
        border: 'none',
        borderRadius: radii.xs,
        color: admin.caption,
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}
